/**
 * The SRD monsters have English names only. The common ones have a Russian name here — for the search of the
 * bestiary (a typed Russian word becomes the English query the API understands) and for the name a monster gets
 * when it is put on a Russian table («Гоблин 1», not «Goblin»). The list is one: the search and the naming read it.
 */
export const RU_ALIASES: ReadonlyArray<readonly [string, string]> = [
  ['хобгоблин', 'hobgoblin'], ['гоблин', 'goblin'], ['багбир', 'bugbear'], ['орк', 'orc'], ['огр', 'ogre'],
  ['скелет', 'skeleton'], ['зомби', 'zombie'], ['упырь', 'ghoul'], ['призрак', 'ghost'], ['вампир', 'vampire'],
  ['волк', 'wolf'], ['медведь', 'bear'], ['кабан', 'boar'], ['крыса', 'rat'], ['летучая мышь', 'bat'], ['паук', 'spider'],
  ['змея', 'snake'], ['тролль', 'troll'], ['великан', 'giant'], ['дракон', 'dragon'], ['кобольд', 'kobold'],
  ['бандит', 'bandit'], ['стражник', 'guard'], ['разбойник', 'thug'], ['культист', 'cultist'], ['маг', 'mage'],
  ['жрец', 'priest'], ['рыцарь', 'knight'], ['гриф', 'griffon'], ['минотавр', 'minotaur'], ['сова', 'owl'],
  ['лев', 'lion'], ['тигр', 'tiger'], ['акула', 'shark'], ['слизь', 'ooze'], ['элементаль', 'elemental'],
  ['гарпия', 'harpy'], ['мантикора', 'manticore'], ['мумия', 'mummy'], ['гнолл', 'gnoll'], ['ящер', 'lizard'],
];

/** «Гоблин» for the SRD monster named exactly «Goblin»; undefined when the name has no Russian one here. */
export function ruMonsterName(englishName: string): string | undefined {
  const key = englishName.trim().toLowerCase();
  const hit = RU_ALIASES.find(([, en]) => en === key);
  if (hit === undefined) return undefined;
  const ru = hit[0];
  return ru.charAt(0).toUpperCase() + ru.slice(1);
}

/**
 * The common SRD spells under their Russian names (the Russian PHB words), for the search: «огненный шар» finds
 * «Fireball». The SRD data stays English; a spell without an entry here is found by its English name only.
 */
export const RU_SPELL_NAMES: Readonly<Record<string, string>> = {
  'Fireball': 'Огненный шар', 'Fire Bolt': 'Огненный снаряд', 'Magic Missile': 'Волшебная стрела', 'Cure Wounds': 'Лечение ран',
  'Healing Word': 'Лечащее слово', 'Mage Hand': 'Волшебная рука', 'Mage Armor': 'Доспехи мага', 'Shield': 'Щит', 'Sleep': 'Усыпление',
  'Light': 'Свет', 'Detect Magic': 'Обнаружение магии', 'Eldritch Blast': 'Мистический заряд', 'Sacred Flame': 'Священное пламя',
  'Guidance': 'Указание', 'Bless': 'Благословение', 'Thunderwave': 'Волна грома', 'Burning Hands': 'Огненные ладони',
  'Charm Person': 'Очарование личности', 'Hold Person': 'Удержание личности', 'Hold Monster': 'Удержание чудовища',
  'Misty Step': 'Туманный шаг', 'Invisibility': 'Невидимость', 'Greater Invisibility': 'Высшая невидимость', 'Lightning Bolt': 'Молния',
  'Counterspell': 'Контрзаклинание', 'Dispel Magic': 'Рассеивание магии', 'Fly': 'Полёт', 'Haste': 'Ускорение', 'Slow': 'Замедление',
  'Revivify': 'Возрождение', 'Raise Dead': 'Оживление', 'Spirit Guardians': 'Духовные стражи', 'Spiritual Weapon': 'Божественное оружие',
  'Polymorph': 'Превращение', 'Dimension Door': 'Переносящая дверь', 'Teleport': 'Телепортация', 'Wish': 'Исполнение желаний',
  'Web': 'Паутина', 'Darkness': 'Тьма', 'Silence': 'Тишина', 'Fog Cloud': 'Туманное облако', 'Entangle': 'Опутывание',
  'Faerie Fire': 'Огонь фей', 'Ray of Frost': 'Луч холода', 'Shocking Grasp': 'Электрошок', 'Chill Touch': 'Леденящее прикосновение',
  'Vicious Mockery': 'Злая насмешка', 'Spare the Dying': 'Уход за умирающим', 'Prestidigitation': 'Фокусы', 'Minor Illusion': 'Малая иллюзия',
  'Identify': 'Опознание', 'Command': 'Приказ', "Hunter's Mark": 'Метка охотника', 'Scorching Ray': 'Палящий луч',
  'Cone of Cold': 'Конус холода', 'Wall of Fire': 'Огненная стена', 'Banishment': 'Изгнание', 'Heal': 'Полное исцеление',
  'Sanctuary': 'Убежище', 'Guiding Bolt': 'Направленный снаряд', 'Shatter': 'Дребезги', 'Suggestion': 'Внушение',
  'Speak with Dead': 'Разговор с мёртвыми', 'Animate Dead': 'Восставший труп', 'Fear': 'Ужас', 'Blur': 'Размытый образ',
  'Mirror Image': 'Зеркальные отражения', 'Lesser Restoration': 'Малое восстановление', 'Greater Restoration': 'Высшее восстановление',
  'Bane': 'Порча', 'Thaumaturgy': 'Чудотворство', 'Druidcraft': 'Искусство друидов', 'Mending': 'Починка', 'Message': 'Сообщение',
  'Poison Spray': 'Ядовитые брызги', 'Acid Splash': 'Брызги кислоты', 'Feather Fall': 'Падение пёрышком', 'Jump': 'Прыжок',
  'Find Familiar': 'Поиск фамильяра', 'Comprehend Languages': 'Понимание языков', 'Disguise Self': 'Маскировка',
  'Ice Storm': 'Град', 'Stoneskin': 'Каменная кожа', 'Meteor Swarm': 'Метеоритный дождь', 'Power Word Kill': 'Слово силы: смерть',
};

/** «Огненный шар» for «Fireball»; undefined when the spell has no Russian name here. */
export function ruSpellName(englishName: string): string | undefined {
  return RU_SPELL_NAMES[englishName];
}

/**
 * The Russian words a monster is found by: every alias whose English word is a word of its name — «Adult Red Dragon»
 * is found by «дракон», «Giant Spider» by «паук» and «великан» (the search matches words, so «Hobgoblin» is not
 * found by «гоблин» through this, only by its own «хобгоблин»).
 */
export function ruMonsterAliases(englishName: string): string[] {
  const words = englishName.toLowerCase().split(/[^a-z]+/).filter(Boolean);
  const whole = englishName.trim().toLowerCase();
  return RU_ALIASES.filter(([, en]) => en === whole || (!en.includes(' ') && words.includes(en))).map(([ru]) => ru);
}

/** SRD monster types and the common subtypes in Russian — the Bestiary is searched by them too («нежить», «демон»). */
const RU_MONSTER_KINDS: Readonly<Record<string, string>> = {
  aberration: 'аберрация', beast: 'зверь', celestial: 'небожитель', construct: 'конструкт', dragon: 'дракон',
  elemental: 'элементаль', fey: 'фея', fiend: 'исчадие', giant: 'великан', humanoid: 'гуманоид', monstrosity: 'чудовище',
  ooze: 'слизь', plant: 'растение', undead: 'нежить', swarm: 'рой', devil: 'дьявол', demon: 'демон', shapechanger: 'оборотень',
  goblinoid: 'гоблиноид', human: 'человек', titan: 'титан',
};

/** The words a monster's type / subtype give it: «undead» and «нежить», «swarm of Tiny beasts» → «swarm», «рой». */
export function monsterKindNames(type: string, subtype?: string): string[] {
  const out: string[] = [];
  for (const raw of [type, subtype ?? '']) {
    const value = raw.trim().toLowerCase();
    if (value === '' || value === 'any race') continue;
    out.push(value);
    const head = value.split(' ')[0] ?? value;
    const ru = RU_MONSTER_KINDS[value] ?? RU_MONSTER_KINDS[head];
    if (ru !== undefined) out.push(ru);
  }
  return out;
}
